import { z } from 'zod'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE CONSOLE'S VIEW OF A REVIEW DRAFT — `SPEC-LGH-001`.
 *
 * Structurally `CTR-002` §V3, with **one arm the wire can never produce**:
 * `unreadable`. That arm is the whole reason this file exists rather than the
 * frontend consuming the contract shapes directly.
 *
 * ── WHY A FOURTH ARM, AND WHY HERE ─────────────────────────────────────────
 * `LGH-007` Q3 asked what the screen does when the platform emits a class the
 * console does not know. The wire union is closed and `.strict()`, so a fourth
 * class is a parse failure — and `apiGet` parses the whole response, which
 * means **a twelve-line quote with one unreadable price would blank all
 * twelve.**
 *
 * The answer is a two-stage parse: structure strictly, each field
 * individually, and a field that fails becomes `unreadable`. That has to
 * happen where the console FIRST meets platform data — the backend — because a
 * strict parse there would reject the response before the frontend could
 * degrade anything.
 *
 * **`unreadable` is not inferring a class.** Inferring asserts something about
 * the draft the platform did not say. Declaring a field unreadable asserts
 * something about *the console* — that this build cannot read what arrived —
 * which is the one claim the console is entitled to make and is always true
 * when made.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export const reviewFieldSchema = z.enum(['item', 'quantity', 'unitPrice'])
export type ReviewField = z.infer<typeof reviewFieldSchema>

const evidenceBase = { says: z.string() }
/**
 * `LGH-018` §1, `R-075`. The instant the catalogue was actually read, pinned
 * at parse time on the platform and served verbatim — never a fresh clock
 * read at review time. See `contractCatalogEvidenceSchema`'s own comment for
 * the full argument; carried here unchanged, camelCased like every other
 * field crossing this boundary.
 */
const catalogueBacked = { catalogId: z.string(), catalogName: z.string(), readAt: z.string() }

export const reviewEvidenceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('catalog'), ...evidenceBase, ...catalogueBacked }),
  z.object({
    kind: z.literal('tenantVocabulary'),
    ...evidenceBase,
    ...catalogueBacked,
    taughtAt: z.string(),
  }),
  /** Candidate-only on the wire; the type allows it here and the parse never produces it on a resolution. */
  z.object({ kind: z.literal('sharedSuggestion'), ...evidenceBase, ...catalogueBacked }),
  /** The customer's own words. No catalogue identity, because there is no catalogue row. */
  z.object({ kind: z.literal('rfqSpan'), ...evidenceBase, span: z.string() }),
  /** Amendment 5 — who settled this field, and when. */
  z.object({
    kind: z.literal('operatorDecision'),
    ...evidenceBase,
    decidedAt: z.string(),
    operator: z.object({ id: z.string(), name: z.string() }),
  }),
])
export type ReviewEvidence = z.infer<typeof reviewEvidenceSchema>

export const reviewCandidateSchema = z.object({
  candidateId: z.string(),
  value: z.string(),
  evidence: reviewEvidenceSchema,
})
export type ReviewCandidate = z.infer<typeof reviewCandidateSchema>

export const sourcedSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('grounded'), value: z.string(), evidence: reviewEvidenceSchema }),
  /**
   * `candidates` is always present here and may be empty — the per-field split
   * lives on the WIRE, where it stops the platform expressing a state that
   * cannot occur. Inside the console one shape renders every field, and the
   * screen shows a picker when there is something to pick.
   *
   * **That is derived presentation, not derived classification** (`LGH-006`
   * §2.1). Showing a picker because candidates exist cannot contradict the
   * platform; inferring `unmatched` from an empty array would.
   */
  z.object({
    kind: z.literal('asked'),
    question: z.string(),
    candidates: z.array(reviewCandidateSchema),
  }),
  z.object({
    kind: z.literal('unmatched'),
    reason: z.object({ code: z.string(), says: z.string() }),
  }),
  /**
   * THE ARM THE WIRE CANNOT PRODUCE.
   *
   * `says` is the console's own sentence and the only one on this screen it
   * authors — see `SPEC-LGH-001` §2.3 as amended, and the list in
   * `LGH-009`'s report. It is version-mismatch wording rather than outage
   * wording, because that is what it is.
   */
  z.object({ kind: z.literal('unreadable'), says: z.string() }),
])
export type Sourced = z.infer<typeof sourcedSchema>

export const reviewLineSchema = z.object({
  lineId: z.string(),
  /** The whole line as the customer wrote it — not the same as `rfqSpan.span`. */
  rawText: z.string(),
  item: sourcedSchema,
  quantity: sourcedSchema,
  unitPrice: sourcedSchema,
})
export type ReviewLine = z.infer<typeof reviewLineSchema>

export const outstandingFieldSchema = z.object({
  lineId: z.string(),
  field: reviewFieldSchema,
})
export type OutstandingField = z.infer<typeof outstandingFieldSchema>

export const reviewDraftSchema = z.object({
  lines: z.array(reviewLineSchema),
  /** What an approval would bind to. */
  fingerprint: z.string(),
  outstanding: z.array(outstandingFieldSchema),
  /**
   * **Null since `LGH-021`**, because the contract's `ReviewResponse` carries
   * no `as_of` — unlike every other read. The screen shows no as-of rather
   * than inventing one from the time of receipt, which would be a claim about
   * the platform made by the console.
   */
  asOf: z.string().nullable(),
})
export type ReviewDraft = z.infer<typeof reviewDraftSchema>

// ── the write path ─────────────────────────────────────────────────────────

export const reviewChoiceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('candidate'), candidateId: z.string() }),
  z.object({ kind: z.literal('freeText'), text: z.string().min(1) }),
  z.object({ kind: z.literal('addToCatalog'), text: z.string().min(1) }),
])
export type ReviewChoice = z.infer<typeof reviewChoiceSchema>

export const reviewDecisionSchema = z.object({
  lineId: z.string(),
  field: reviewFieldSchema,
  choice: reviewChoiceSchema,
})
export type ReviewDecision = z.infer<typeof reviewDecisionSchema>

/**
 * **NO OPERATOR FIELD — amendment 4, and the absence is the point.**
 *
 * The console resolves identity from `request.auth`, exactly as
 * `routes/runs.ts:160` already does for `approve` under a comment naming
 * *"the cheapest possible forgery"*. There is no field here in which a browser
 * could claim to be someone else: a field that does not exist cannot carry the
 * mistake, which is the same argument as `acceptAllKnown` carrying no ids.
 */
export const reviewDecisionsRequestSchema = z.object({
  draftFingerprint: z.string().min(1),
  acceptAllKnown: z.boolean(),
  decisions: z.array(reviewDecisionSchema),
})
export type ReviewDecisionsRequest = z.infer<typeof reviewDecisionsRequestSchema>
